import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Select,
  Spacer,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
} from "@chakra-ui/react";
import {
  BiCalendarAlt,
  BiCamera,
  BiMap,
  BiPhone,
  BiPlus,
  BiTrash,
  BiUser,
} from "react-icons/bi";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import LocationPicker from "components/shared/locationPicker";
import { useLangContext } from "configs/languageContext";
import { form, form_fields } from "data/translations";
import { accident } from "data/data";
import { TemplateForm } from "components/layout/template";
import { useDataContext } from "configs/dataContext";
import processFormData from "./processFormData";

const C = {
  primary: "#059669",
  primaryDark: "#047857",
  primaryLight: "#ECFDF5",
  primarySoft: "#D1FAE5",
  emergency: "#DC2626",
  emergencySoft: "#FEE2E2",
  ink: "#0F172A",
  text: "#1E293B",
  muted: "#64748B",
  mutedLight: "#94A3B8",
  border: "#E2E8F0",
  bg: "#F8FAFC",
};

// ── shared input styling ──
const inputProps = {
  size: "lg" as const,
  bg: "white",
  borderColor: C.border,
  focusBorderColor: C.primary,
  borderRadius: "lg" as const,
  fontSize: "md" as const,
};

const labelProps = {
  fontWeight: "600" as const,
  color: C.ink,
  fontSize: "sm" as const,
  mb: 1.5,
};

interface SectionProps {
  number: number;
  title: string;
  helper?: string;
  children: React.ReactNode;
}
const Section = ({ number, title, helper, children }: SectionProps) => (
  <Box>
    <HStack mb={4} spacing={3} align="center">
      <Flex
        w="32px" h="32px"
        align="center" justify="center"
        bg={C.primaryLight} color={C.primary}
        borderRadius="full"
        fontWeight="800" fontSize="sm"
      >
        {number}
      </Flex>
      <Box>
        <Heading as="h2" size="md" fontWeight="700" color={C.ink} letterSpacing="-0.01em">
          {title}
        </Heading>
        {helper && <Text fontSize="xs" color={C.muted} mt={0.5}>{helper}</Text>}
      </Box>
    </HStack>
    <VStack spacing={4} align="stretch">{children}</VStack>
  </Box>
);

const RequiredAsterisk = () => (
  <Text as="span" color={C.emergency} fontWeight="700" ml={0.5}>*</Text>
);

export default function PublicFormPage() {
  const { lang } = useLangContext();
  const { addData } = useDataContext();
  const t = (key: string) => form[key]?.[lang] ?? key;

  const {
    isOpen: isVisibleFail,
    onClose: onCloseFail,
    onOpen: onOpenFail,
  } = useDisclosure({ defaultIsOpen: false });
  const {
    isOpen: isVisibleSuccess,
    onClose: onCloseSuccess,
    onOpen: onOpenSuccess,
  } = useDisclosure({ defaultIsOpen: false });

  const {
    register,
    control,
    watch,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
    setValue,
    getValues,
  } = useForm<accident>();

  const onSubmit: SubmitHandler<accident> = async (data: accident) => {
    onCloseFail();
    delete data.picture_curr;
    try {
      const processedData = processFormData(data, "visitor");
      await addData(processedData);
      reset();
      onOpenSuccess();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.log("Error adding document: ", e);
      onOpenFail();
    }
  };

  const handleImageUpload = () => {
    let img = getValues("picture_curr");
    if (img && img.length > 0) {
      let files: any = getValues("picture_links") || [];
      files.push(img[0]);
      setValue("picture_links", files);
    }
  };
  const handleImageDelete = (ind: number) => {
    const cur = getValues("picture_links");
    setValue("picture_links", [...cur.slice(0, ind), ...cur.slice(ind + 1)]);
  };

  const picCount = Array.isArray(watch("picture_links")) ? watch("picture_links").length : 0;

  return (
    <TemplateForm>
      {/* SUCCESS */}
      {isVisibleSuccess && (
        <Alert
          status="success"
          mb={6}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={C.primarySoft}
          bg={C.primaryLight}
        >
          <AlertIcon color={C.primary} />
          <Box flex="1">
            <AlertTitle color={C.ink}>{t("popup_success_title")}</AlertTitle>
            <AlertDescription color={C.muted} fontSize="sm">
              {t("popup_success_text")}
            </AlertDescription>
          </Box>
          <CloseButton onClick={onCloseSuccess} />
        </Alert>
      )}

      {/* Intro */}
      <Text fontSize="md" color={C.muted} mb={6} lineHeight="1.6">
        {t("intro")}
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={10} align="stretch">

          {/* 1 — What happened */}
          <Section number={1} title={t("accident")} helper={t("required_hint")}>
            {/* Date */}
            <FormControl>
              <FormLabel {...labelProps}>
                <HStack spacing={2}><BiCalendarAlt /><Text>{t("accident_timestamp")}</Text></HStack>
              </FormLabel>
              <Controller
                control={control}
                name="accident_timestamp"
                defaultValue={new Date()}
                render={({ field }) => {
                  const d = field.value instanceof Date
                    ? field.value
                    : field.value ? new Date(field.value as any) : new Date();
                  const iso = isNaN(d.getTime()) ? new Date().toISOString().slice(0, 10) : d.toISOString().slice(0, 10);
                  const today = new Date().toISOString().slice(0, 10);
                  return (
                    <Input
                      {...inputProps}
                      type="date"
                      value={iso}
                      max={today}
                      onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : new Date())}
                    />
                  );
                }}
              />
            </FormControl>

            {/* Type */}
            <FormControl isInvalid={Boolean(errors.accident_type)}>
              <FormLabel {...labelProps}>
                {t("accident_type")}<RequiredAsterisk />
              </FormLabel>
              <Select
                {...inputProps}
                placeholder={lang === "fr" ? "Choisir…" : lang === "en" ? "Choose…" : "Elegir…"}
                {...register("accident_type", { required: true })}
              >
                {Object.keys(form_fields.accident_type).map((k: any) => (
                  <option key={k} value={k}>
                    {form_fields.accident_type[k][lang] ?? form_fields.accident_type[k].es}
                  </option>
                ))}
              </Select>
              <FormErrorMessage fontSize="xs">{t("field_error")}</FormErrorMessage>
            </FormControl>

            {/* Animal condition */}
            <FormControl isInvalid={Boolean(errors.animal_condition)}>
              <FormLabel {...labelProps}>
                {t("animal_condition")}<RequiredAsterisk />
              </FormLabel>
              <Select
                {...inputProps}
                placeholder={lang === "fr" ? "Choisir…" : lang === "en" ? "Choose…" : "Elegir…"}
                {...register("animal_condition", { required: true })}
              >
                {Object.keys(form_fields.animal_condition).map((k: any) => (
                  <option key={k} value={k}>
                    {form_fields.animal_condition[k][lang] ?? form_fields.animal_condition[k].es}
                  </option>
                ))}
              </Select>
              <FormErrorMessage fontSize="xs">{t("field_error")}</FormErrorMessage>
            </FormControl>
          </Section>

          {/* 2 — Where */}
          <Section number={2} title={t("accident_location")} helper={t("required_hint")}>
            <FormControl isInvalid={Boolean(errors.accident_location)}>
              <FormLabel {...labelProps}>
                <HStack spacing={2}><BiMap /><Text>{t("accident_location")}</Text></HStack>
                <RequiredAsterisk />
              </FormLabel>
              <FormHelperText fontSize="xs" color={C.muted} mt={-1} mb={2}>
                {t("accident_location_help")}
              </FormHelperText>
              <Box
                borderRadius="lg"
                borderWidth={errors.accident_location ? "2px" : "1px"}
                borderColor={errors.accident_location ? C.emergency : C.border}
                overflow="hidden"
              >
                <Controller
                  name="accident_location"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => <LocationPicker field={field} />}
                />
              </Box>
              <FormErrorMessage fontSize="xs">{t("field_error")}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("accident_site_details_locality")}</FormLabel>
              <FormHelperText fontSize="xs" color={C.muted} mt={-1} mb={2}>
                {t("accident_site_details_locality_help")}
              </FormHelperText>
              <Textarea
                {...inputProps}
                rows={2}
                {...register("accident_site_details_locality", {})}
              />
            </FormControl>
          </Section>

          {/* 3 — Photos */}
          <Section number={3} title={t("picture_links")} helper={t("picture_links_help")}>
            <Box
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={C.border}
              borderRadius="xl"
              bg={C.bg}
              p={6}
              textAlign="center"
              _hover={{ borderColor: C.primary, bg: C.primaryLight }}
              transition="all .15s ease"
              position="relative"
              cursor="pointer"
            >
              <Input
                type="file"
                accept="image/*;capture=camera"
                multiple={false}
                opacity={0}
                position="absolute"
                inset={0}
                cursor="pointer"
                {...register("picture_curr", { onChange: handleImageUpload })}
                aria-label={t("add_photo")}
              />
              <VStack spacing={2} pointerEvents="none">
                <Flex w="44px" h="44px" align="center" justify="center" bg="white" borderRadius="full" boxShadow="sm" color={C.primary}>
                  <BiCamera size="24" />
                </Flex>
                <Text fontSize="sm" fontWeight="700" color={C.ink}>
                  {t("add_photo")}
                </Text>
                {picCount > 0 ? (
                  <Text fontSize="xs" color={C.muted}>
                    {picCount} {t("length")}
                  </Text>
                ) : (
                  <Text fontSize="xs" color={C.muted}>
                    {t("picture_links_help")}
                  </Text>
                )}
              </VStack>
            </Box>

            {picCount > 0 && (
              <Wrap spacing={3} mt={1}>
                {watch("picture_links").map((file: any, ind: number) => (
                  <WrapItem key={"image_" + ind}>
                    <Box position="relative" borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor={C.border}>
                      <Image
                        h="120px"
                        w="120px"
                        objectFit="cover"
                        src={typeof file === "string" ? file : URL.createObjectURL(file)}
                        alt=""
                      />
                      <IconButton
                        onClick={() => handleImageDelete(ind)}
                        icon={<BiTrash />}
                        size="xs"
                        position="absolute"
                        top={1.5}
                        right={1.5}
                        bg="white"
                        color={C.emergency}
                        _hover={{ bg: C.emergencySoft }}
                        aria-label={t("delete_photo")}
                        borderRadius="full"
                        boxShadow="sm"
                      />
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Section>

          {/* 4 — Contact */}
          <Section number={4} title={t("personal")} helper={t("personal_help")}>
            <FormControl>
              <FormLabel {...labelProps}>
                <HStack spacing={2}><BiUser /><Text>{t("person_reporting")}</Text></HStack>
              </FormLabel>
              <Input {...inputProps} type="text" {...register("person_reporting", {})} />
            </FormControl>

            <HStack spacing={3} align="flex-start" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }} isInvalid={Boolean(errors.person_reporting_email)}>
                <FormLabel {...labelProps}>{t("person_reporting_email")}</FormLabel>
                <Input {...inputProps} type="email" {...register("person_reporting_email", {})} />
              </FormControl>

              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>
                  <HStack spacing={2}><BiPhone /><Text>{t("person_reporting_phone")}</Text></HStack>
                </FormLabel>
                <Input {...inputProps} type="tel" {...register("person_reporting_phone", {})} />
              </FormControl>
            </HStack>
          </Section>

          {/* 5 — Comments */}
          <Section number={5} title={t("comments_header")} helper={t("optional_hint")}>
            <FormControl>
              <FormLabel {...labelProps}>{t("comments")}</FormLabel>
              <Textarea {...inputProps} rows={3} {...register("comments", {})} />
            </FormControl>
          </Section>

          {/* SUBMIT */}
          <VStack spacing={3} pt={2}>
            <Button
              type="submit"
              w="100%"
              h={{ base: "62px", md: "72px" }}
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="800"
              letterSpacing="-0.01em"
              bg={C.primary}
              color="white"
              _hover={{ bg: C.primaryDark, transform: "translateY(-1px)", boxShadow: "xl" }}
              _active={{ transform: "translateY(0)" }}
              borderRadius="xl"
              boxShadow={`0 12px 28px ${C.primarySoft}`}
              isLoading={isSubmitting}
              loadingText={t("submitting")}
              onClick={() => onOpenFail()}
            >
              {t("submit")}
            </Button>
            <Button
              as={Link} to="/" variant="ghost"
              fontSize="sm" color={C.muted}
              _hover={{ color: C.primary, bg: "transparent" }}
            >
              ← {t("go_back")}
            </Button>
          </VStack>
        </VStack>
      </form>

      {/* FAILURE alert */}
      <Alert
        status="error"
        position="fixed"
        bottom={4}
        left="50%"
        transform="translateX(-50%)"
        maxW="600px"
        borderRadius="xl"
        zIndex={10000}
        boxShadow="2xl"
        display={isVisibleFail && Object.keys(errors).length > 0 ? "flex" : "none"}
      >
        <AlertIcon />
        <Box flex="1">
          <AlertTitle fontSize="sm">{t("popup_error_title")}</AlertTitle>
          <AlertDescription fontSize="xs">{t("popup_error_text")}</AlertDescription>
        </Box>
        <Spacer />
        <CloseButton onClick={onCloseFail} />
      </Alert>
    </TemplateForm>
  );
}
